import React, { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { Header, Footer, TopPromoBanner } from '@amzn/burberry-ui-components';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE, TRANSLATION_ROUTE } from '../constants';
import arbManifest from '../i18n/translations/arbManifest';
import { store, AppDispatch } from 'src/store/store';
import { Paths } from 'src/paths/Paths';
import { UH_OH, NOT_FOUND_STRING, GENERIC_ERROR_STRING } from '@amzn/d2c-application-website-common';
import { getSite, getVersion, getIsPreview, fetchSiteConfig, generateCSSVariables } from 'src/utils/siteUtils';
import { getHeaderProps, getFooterProps, getTopPromoBannerProps } from 'src/utils/templateProps';
import { metricEmitter } from 'src/utils/metricEmitter';
import { getStage } from 'src/utils/roadieHelper';
import { setTemplate, setTemplateLoading, setTemplateError, selectTemplate, selectTemplateError } from 'src/store/templateSlice';
import { selectCart } from 'src/store/cartSlice';
import { selectExpiryTime, selectCsrfToken, logOut } from 'src/store/authSlice';
import { setEngagementId } from 'src/services/engagementId';
import { awsRum } from 'src/rum';
import CloseWindow from './CloseWindow';

import Home from 'src/pages/Home/Home';
import ProductDetail from 'src/pages/ProductDetail/ProductDetail';
import Cart from 'src/pages/Cart/Cart';
import Order from 'src/pages/Order/Order';
import Success from 'src/pages/Success/Success';
import ErrorPage from 'src/pages/Error/Error';
import FAQ from 'src/pages/FAQ/FAQ';
import Terms from 'src/pages/Terms/Terms';
import PrivacyPolicy from 'src/pages/PrivacyPolicy/PrivacyPolicy';

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const template = useSelector(selectTemplate);
  const templateError = useSelector(selectTemplateError);
  const cart = useSelector(selectCart);
  const expiryTime = useSelector(selectExpiryTime);
  const csrfToken = useSelector(selectCsrfToken);
  const [siteReady, setSiteReady] = useState(false);
  const stage = getStage();
  const isPreview = getIsPreview();
  const env = isPreview ? 'PREVIEW' : 'LIVE';
  const siteId = getSite(stage, env);
  const version = getVersion(stage, env) || 'LIVE';
  const isLoggedIn = expiryTime ? Date.now() < expiryTime : false;

  // Fetch site template on mount.
  // - PREVIEW mode: calls Mulberry internal API to get draft template from DDB.
  // - LIVE mode: fetches promoted config from S3 via CloudFront.
  useEffect(() => {
    if (!siteId) {
      setSiteReady(true);
      return;
    }

    dispatch(setTemplateLoading(true));
    fetchSiteConfig(siteId, version, isPreview)
      .then((data) => {
        dispatch(setTemplate(data));
        if (data.engagementId) {
          setEngagementId(data.engagementId);
        }
        setSiteReady(true);
      })
      .catch((error) => {
        console.error('Failed to load site config:', error);
        awsRum?.recordEvent('TemplateError', { message: error.message, siteId });
        dispatch(setTemplateError(error.message));
        setSiteReady(true);
      });
  }, [dispatch, siteId, version]);

  useEffect(() => {
    if (!template || !siteId) return;

    const styleId = `site-config-${siteId}`;
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) existingStyle.remove();

    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = generateCSSVariables(siteId, template);
    document.head.appendChild(styleElement);

    document.documentElement.setAttribute('site-id', siteId);

    if (template.title) {
      document.title = template.title;
    }

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [template, siteId]);

  if (!siteReady) {
    return null;
  }

  if (templateError) {
    return <ErrorPage heading={UH_OH} message={GENERIC_ERROR_STRING} pageName="SiteLoadError" />;
  }

  if (!template) {
    return <div className="app" data-testid="app" />;
  }

  const headerProps = getHeaderProps(template);
  const footerProps = getFooterProps(template);
  const topPromoBannerProps = getTopPromoBannerProps(template);

  return (
    <div className="app" data-testid="app">
      <Header {...headerProps} cartItemCount={cart?.quantity ?? 0} onCartClick={() => navigate('/cart')}
              onNavLinkClick={(url) => navigate(url)} isLoggedIn={isLoggedIn}
              onLogoutClick={async () => { try { await dispatch(logOut({ stage: getStage(), csrfToken })); } finally { localStorage.removeItem('authState'); window.location.href = Paths.home; } }} metricEmitter={metricEmitter}/>
      <TopPromoBanner {...topPromoBannerProps} metricEmitter={metricEmitter} />
      <main className="app__content">
        <Routes>
          <Route path={Paths.home} element={<Home />} />
          <Route path={Paths.details} element={<ProductDetail />} />
          <Route path={Paths.cart} element={<Cart />} />
          <Route path={Paths.previewCheckout} element={<Order />} />
          <Route path={Paths.successCheckout} element={<Success />} />
          <Route path={Paths.faq} element={<FAQ />} />
          <Route path={Paths.terms} element={<Terms />} />
          <Route path={Paths.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={Paths.invalid} element={<ErrorPage heading={UH_OH} message={NOT_FOUND_STRING} pageName="NotFound" />} />
        </Routes>
      </main>
      <Footer {...footerProps} metricEmitter={metricEmitter} />
    </div>
  );
};

export const App: React.FC = () => {
  if (window.location.pathname === Paths.closeWindow) {
    return <CloseWindow />;
  }

  const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
      .withLocale(DEFAULT_LOCALE)
      .withDefaultLocale(DEFAULT_LOCALE)
      .build(),
    urlPrefix: TRANSLATION_ROUTE,
  };

  return (
    <Provider store={store}>
      <MbmProvider {...mbmOptions}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </MbmProvider>
    </Provider>
  );
};

export default App;
